import { useCallback, useEffect, useState } from 'react';
import { attendanceAPI, departmentsAPI } from '../services/api';
import { CalendarDays, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const emptyHolidayForm = {
  name: '',
  holiday_date: '',
  department_id: '',
  is_active: true,
};

const Holidays = () => {
  const { hasPermission } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyHolidayForm);

  const loadData = useCallback(async () => {
    const [holidaysRes, departmentsRes] = await Promise.all([
      attendanceAPI.getHolidays(),
      departmentsAPI.getAll(),
    ]);
    setHolidays(holidaysRes.data.holidays || []);
    setDepartments(departmentsRes.data.departments || []);
  }, []);

  useEffect(() => {
    loadData().catch(() => toast.error('Failed to load holidays'));
  }, [loadData]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyHolidayForm);
    setShowForm(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      const payload = {
        ...form,
        department_id: form.department_id || null,
      };

      if (editingId) {
        await attendanceAPI.updateHoliday(editingId, payload);
        toast.success('Holiday updated');
      } else {
        await attendanceAPI.createHoliday(payload);
        toast.success('Holiday created');
      }

      resetForm();
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save holiday');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (holiday) => {
    setEditingId(holiday.id);
    setForm({
      name: holiday.name || '',
      holiday_date: String(holiday.holiday_date || '').slice(0, 10),
      department_id: holiday.department_id || '',
      is_active: holiday.is_active,
    });
    setShowForm(true);
  };

  const handleDeactivate = async (holiday) => {
    try {
      await attendanceAPI.updateHoliday(holiday.id, {
        name: holiday.name,
        holiday_date: String(holiday.holiday_date).slice(0, 10),
        department_id: holiday.department_id,
        is_active: false,
      });
      toast.success('Holiday deactivated');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to deactivate holiday');
    }
  };

  const canManage = hasPermission('holidays.manage');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Holidays</h1>
          <p className="text-gray-500 text-sm mt-1">Manage holidays that affect attendance evaluation.</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowForm((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            {showForm ? 'Close Form' : 'Add Holiday'}
          </button>
        )}
      </div>

      {showForm && canManage && (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Holiday Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Republic Day"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Holiday Date</label>
            <input
              type="date"
              value={form.holiday_date}
              onChange={(e) => setForm({ ...form, holiday_date: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Department</label>
            <select
              value={form.department_id}
              onChange={(e) => setForm({ ...form, department_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Global holiday</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>{department.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={form.is_active ? 'active' : 'inactive'}
              onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="md:col-span-2 flex gap-3">
            <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving...' : editingId ? 'Update Holiday' : 'Create Holiday'}
            </button>
            <button type="button" onClick={resetForm} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {holidays.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No holidays found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Holiday</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Department</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {holidays.map((holiday) => (
                <tr key={holiday.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <CalendarDays size={16} className="text-blue-600" />
                      {holiday.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{new Date(holiday.holiday_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-gray-600">{holiday.department_name || 'Global'}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${holiday.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {holiday.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {canManage ? (
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(holiday)} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50">
                          <Pencil size={12} />
                          Edit
                        </button>
                        {holiday.is_active && (
                          <button onClick={() => handleDeactivate(holiday)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                            <Trash2 size={12} />
                            Deactivate
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Holidays;
